import { useMemo } from 'react';

import { useGivePointsCapabilities } from '@bonusly/common-give-box';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Bonus } from '../../../../types/bonus';
import { UserMode } from '../../../../types/user';
import { isWithinEditWindow } from '../utils/bonus';

export const useContextualizedBonusDetails = (bonus: Bonus | null) => {
	const user = useCurrentUser();
	const { canGiveAmount } = useGivePointsCapabilities();

	return useMemo(() => {
		if (!bonus) {
			return {
				isCreatedByUser: false,
				isEditableByUser: false,
				isGiverHuman: false,
				isInitiatedByUser: false,
				isUserOnlyReceiver: false,
				isUserReceiver: false,
				isUserReceiversManager: false,
				canUserAddOn: false,
				onlyOneReceiver: false,
			};
		}

		const receivers = bonus.receivers ?? [];
		const isUserReceiver = receivers.some(({ id }) => id === user.id);
		const isUserReceiversManager = receivers.some(({ id }) => user.direct_report_ids.includes(id));

		const isInitiatedByUser = bonus?.initiator?.id === user.id;
		const isProxyGiver = bonus?.proxy_giver_id != null && bonus.proxy_giver_id === user.id;
		const isCreatedByUser = isInitiatedByUser || bonus?.giver.id === user.id || isProxyGiver;
		const onlyOneReceiver = receivers.length === 1;
		const isUserOnlyReceiver = isUserReceiver && onlyOneReceiver;
		// Not a receiver and can give an amount (non-zero mode + points to give)
		const canUserAddOn = !isUserOnlyReceiver && canGiveAmount;
		const isGiverHuman = bonus.giver.user_mode === UserMode.Normal || bonus.giver.user_mode === UserMode.Benefactor;
		const isEditableByUser = isCreatedByUser && bonus.editable && isWithinEditWindow(bonus);

		return {
			isCreatedByUser,
			isEditableByUser,
			isGiverHuman,
			isInitiatedByUser,
			isUserOnlyReceiver,
			isUserReceiver,
			isUserReceiversManager,
			canUserAddOn,
			onlyOneReceiver,
		};
	}, [bonus, canGiveAmount, user]);
};
